import { ButtonTheme } from 'components/Button';
import { HeaderToolbar } from 'components/HeaderToolbar';
import { ICONS } from 'components/Icon';
import { NavigationHeader } from 'components/NavigationHeader';
import { Menu } from 'components/Menu';
import { Txt } from 'components/Txt';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { SidebarShowButton } from 'componentsAware/SidebarShowButton';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { randomKey } from 'lib/riskbase';
import { theme } from 'lib/theme';
import * as React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  doEngagePropertiesLoad,
  doEngagePropertiesSetFilter,
} from 'store/remote/engageActions';
import {
  selectEngagePropertiesFilterSearch,
  selectEngagePropertiesFilterSortBy,
  selectEngagePropertiesFilterSortDirection,
} from 'store/remote/engageSelectors';
import { EngagePropertiesFilter } from 'types/Engage';
import {
  EngagePropertiesSortDirection,
  EngagePropertiesSortField,
} from 'types/DTOs/BlockManagerDTOs';

type Props = {
  onReloadPress: () => void;
  propertiesCount: number;
  pageKey: string;
};

type SortOption = {
  id: string;
  text: string;
  sortBy: EngagePropertiesSortField | null;
  sortDirection: EngagePropertiesSortDirection | null;
};

const sortOptions: SortOption[] = [
  {
    id: 'all',
    text: 'All Properties',
    sortBy: null,
    sortDirection: null,
  },
  {
    id: 'contacts-desc',
    text: 'Contacts (Most to Least)',
    sortBy: 'contacts',
    sortDirection: 'desc',
  },
  {
    id: 'contacts-asc',
    text: 'Contacts (Least to Most)',
    sortBy: 'contacts',
    sortDirection: 'asc',
  },
  {
    id: 'flats-desc',
    text: 'Flats (Most to Least)',
    sortBy: 'flats',
    sortDirection: 'desc',
  },
  {
    id: 'flats-asc',
    text: 'Flats (Least to Most)',
    sortBy: 'flats',
    sortDirection: 'asc',
  },
];

export const EngagePropertiesHeader: React.FC<Props> = ({
  onReloadPress,
  propertiesCount,
  pageKey,
}) => {
  const dispatch = useDispatch();
  const renderCountRef = React.useRef(0);
  renderCountRef.current += 1;
  const reportTitle = 'Engage';
  const searchValue = useSelector((state) =>
    selectEngagePropertiesFilterSearch(state, pageKey),
  );
  const sortBy = useSelector((state) =>
    selectEngagePropertiesFilterSortBy(state, pageKey),
  );
  const sortDirection = useSelector((state) =>
    selectEngagePropertiesFilterSortDirection(state, pageKey),
  );
  const searchValueRef = React.useRef(searchValue);
  const textfieldKeyRef = React.useRef(randomKey());
  if (searchValueRef.current !== searchValue) {
    textfieldKeyRef.current = randomKey();
    searchValueRef.current = searchValue;
  }

  const handleFilterChange = React.useCallback(
    (filterChanges: Partial<EngagePropertiesFilter>) => {
      dispatch(
        doEngagePropertiesSetFilter({
          key: pageKey,
          data: filterChanges,
        }),
      );
      dispatch(
        doEngagePropertiesLoad({
          key: pageKey,
          page: 'reset',
        }),
      );
    },
    [dispatch, pageKey],
  );

  const handleSearch = React.useCallback(
    (newSearchValue: string) => {
      searchValueRef.current = newSearchValue;
      handleFilterChange({ search: newSearchValue });
    },
    [handleFilterChange],
  );

  const handleSortChange = React.useCallback(
    (
      field: EngagePropertiesSortField | null,
      direction: EngagePropertiesSortDirection | null,
    ) => {
      if (field === sortBy && direction === sortDirection) {
        return;
      }
      handleFilterChange({ sortBy: field, sortDirection: direction });
    },
    [handleFilterChange, sortBy, sortDirection],
  );

  const sortMenuOptions = React.useMemo(
    () =>
      sortOptions.map((option) => ({
        key: option.id,
        text: option.text,
        leftIcon:
          option.sortBy === sortBy && option.sortDirection === sortDirection
            ? ICONS.CircleDotSolid
            : ICONS.CircleLight,
        leftIconColor:
          option.sortBy === sortBy && option.sortDirection === sortDirection
            ? theme.colors.blue
            : theme.colors.blue,
        onPress: () => handleSortChange(option.sortBy, option.sortDirection),
      })),
    [handleSortChange, sortBy, sortDirection],
  );

  return (
    <View>
      <NavigationHeader marginRight={0} marginLeft={0}>
        <SidebarShowButton showSmallScreen marginRight={false} />
        <ViewWithSeparators
          horizontal
          style={{
            alignItems: 'center',
            flex: 1,
          }}
          separatorMargin={theme.paddingHorizontalHalf}
          separatorColor="transparent"
        >
          <Txt
            weight="bold"
            numberOfLines={1}
            style={{ flex: 1, paddingLeft: theme.paddingHorizontal }}
          >
            {reportTitle}{' '}
            <Txt
              numberOfLines={1}
              style={{ color: theme.colors.fieldValueText }}
            >
              {pluralise(
                propertiesCount,
                '{x} Property',
                '{x} Properties',
                'No Properties',
              )}
            </Txt>
          </Txt>
          <Menu
            iconButtonConfig={{ icon: ICONS.EllipsisLight }}
            options={[
              {
                text: 'Reload',
                icon: ICONS.RotateRightLight,
                onPress: onReloadPress,
              },
            ]}
          />
        </ViewWithSeparators>
      </NavigationHeader>
      <HeaderToolbar
        key={textfieldKeyRef.current}
        searchValue={searchValue}
        onSearch={handleSearch}
        searchPlaceholder="Search by property, reference, or location"
        searchDisabled={false}
      >
        <Menu
          buttonConfig={{
            text: 'Sort by',
            iconRight: ICONS.CaretDownSolid,
            iconRightSize: 12,
            textSize: 'xsmall',
            buttonTheme: ButtonTheme.themeless,
            chipLike: true,
          }}
          width={240}
          options={sortMenuOptions}
        />
      </HeaderToolbar>
    </View>
  );
};
